import { markRaw, type Component } from 'vue';
import { useState } from '#imports';
import { AlertType } from '../alert/alert.constants';
import { TOAST_EXIT_DURATION } from './toast.constants';
import type { Toast, ToastOptions } from './toast.types';

const generateId = (): string =>
  `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

const rawComponent = (
  component: Component | undefined,
): Component | undefined =>
  component === undefined ? undefined : markRaw(component);

/**
 * Programmatic toasts, rendered by the `Toaster` that `App` mounts. State lives
 * in Nuxt's `useState` (per request) or its plain-Vue stand-in, so it can be
 * called from components, composables and plain utilities alike. Every toast
 * is kept and times out from creation; the `Toaster` shows only the newest few
 * and reveals older ones as newer ones go. Adding an `id` that already exists
 * updates that toast in place and marks it as a duplicate (the Toaster pulses
 * it). Removal plays the exit transition first.
 */
export const useToast = () => {
  const toasts = useState<Toast[]>('exfluential-ui.toasts', () => []);

  const drop = (id: string): void => {
    toasts.value = toasts.value.filter(
      (item: Toast) => item.id !== id || item.open,
    );
  };

  const remove = (id: string): void => {
    if (toasts.value.some((item: Toast) => item.id === id && !item.open)) {
      return;
    }
    toasts.value = toasts.value.map((item: Toast) =>
      item.id === id ? { ...item, open: false } : item,
    );
    globalThis.setTimeout(() => drop(id), TOAST_EXIT_DURATION);
  };

  const update = (existing: Toast, options: ToastOptions): Toast => {
    const updated: Toast = {
      ...existing,
      ...options,
      id: existing.id,
      type: options.type ?? existing.type,
      component: rawComponent(options.component ?? existing.component),
      open: true,
      duplicate: (existing.duplicate ?? 0) + 1,
    };
    toasts.value = toasts.value.map((item: Toast) =>
      item.id === existing.id ? updated : item,
    );
    return updated;
  };

  const add = (options: ToastOptions): Toast => {
    const existing = toasts.value.find((item: Toast) => item.id === options.id);
    if (existing) {
      return update(existing, options);
    }
    const toast: Toast = {
      ...options,
      id: options.id ?? generateId(),
      type: options.type ?? AlertType.INFO,
      component: rawComponent(options.component),
      open: true,
    };
    toasts.value = [...toasts.value, toast];
    return toast;
  };

  const clear = (): void => {
    toasts.value = [];
  };

  return { toasts, add, remove, clear };
};
